import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  VStack,
  Select,
  useToast,
  Container,
  Heading,
  RadioGroup,
  Radio,
  Stack,
  InputGroup,
  InputLeftElement,
  Icon,
  Center,
  Text,
  HStack,
  Progress,
} from '@chakra-ui/react';
import { cpf, cnpj } from 'cpf-cnpj-validator';
import PasswordValidator from 'password-validator';
import { FiUser, FiMail, FiLock } from 'react-icons/fi';
import { api } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { Logo } from './Logo';

interface RegisterFormData {
  nome: string;
  email: string;
  tipo: 'pessoa' | 'empresa';
  documento: string;
  senha: string;
  confirmarSenha: string;
}

interface PasswordStrength {
  percentage: number;
  passedRules: number;
  validations: string[];
}

interface PasswordRequirement {
  id: string;
  label: string;
  met: boolean;
}

export const Register: React.FC = () => {
  const [formData, setFormData] = useState<RegisterFormData>({
    nome: '',
    email: '',
    tipo: 'pessoa',
    documento: '',
    senha: '',
    confirmarSenha: '',
  });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const toast = useToast();
  const { signIn } = useAuth();
  const [passwordStrength, setPasswordStrength] = useState<PasswordStrength>({
    percentage: 0,
    passedRules: 0,
    validations: [],
  });
  const [passwordRequirements, setPasswordRequirements] = useState<PasswordRequirement[]>([
    { id: 'length', label: 'Mínimo de 8 caracteres', met: false },
    { id: 'uppercase', label: 'Pelo menos uma letra maiúscula', met: false },
    { id: 'lowercase', label: 'Pelo menos uma letra minúscula', met: false },
    { id: 'digits', label: 'Pelo menos um número', met: false },
    { id: 'symbols', label: 'Pelo menos um caractere especial', met: false },
  ]);

  const handleSenhaChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const senha = e.target.value;
    setFormData((prev) => ({ ...prev, senha }));
    const result = calculatePasswordStrength(senha);
    setPasswordStrength(result);

    // Update password requirements status
    setPasswordRequirements((prev) =>
      prev.map((req) => {
        if (req.id === 'length') {
          // Explicitly check for minimum length
          return {
            ...req,
            met: senha.length >= 8,
          };
        }
        // For other requirements, use the validation result
        return {
          ...req,
          met: !result.validations.includes(req.id),
        };
      })
    );
  };

  const schema = new PasswordValidator();
  schema.is().min(8).has().uppercase().has().lowercase().has().digits().has().symbols();

  const calculatePasswordStrength = (password: string) => {
    const validations = schema.validate(password, { list: true }) as string[];
    const totalRules = 5;
    const passedRules = totalRules - validations.length;
    const percentage = (passedRules / totalRules) * 100;
    return { percentage, passedRules, validations };
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleTipoChange = (value: 'pessoa' | 'empresa') => {
    setFormData((prev) => ({ ...prev, tipo: value, documento: '' }));
  };

  const validateForm = () => {
    if (!formData.nome || !formData.email || !formData.documento || !formData.senha) {
      toast({
        title: 'Erro de validação',
        description: 'Por favor, preencha todos os campos obrigatórios.',
        status: 'error',
        duration: 3000,
      });
      return false;
    }

    if (passwordStrength.percentage < 100) {
      toast({
        title: 'Erro de validação',
        description: 'A senha deve atender a todos os requisitos de segurança.',
        status: 'error',
        duration: 3000,
      });
      return false;
    }

    if (formData.senha !== formData.confirmarSenha) {
      toast({
        title: 'Erro de validação',
        description: 'As senhas não coincidem.',
        status: 'error',
        duration: 3000,
      });
      return false;
    }

    const isDocumentoValido =
      formData.tipo === 'pessoa'
        ? cpf.isValid(formData.documento)
        : cnpj.isValid(formData.documento);

    if (!isDocumentoValido) {
      toast({
        title: 'Erro de validação',
        description: `${formData.tipo === 'pessoa' ? 'CPF' : 'CNPJ'} inválido.`,
        status: 'error',
        duration: 3000,
      });
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      setLoading(true);
      // Cadastro público; a API cria a conta com role 'user'.
      await api.post('api/auth/signup', {
        nome: formData.nome,
        email: formData.email,
        senha: formData.senha,
      });

      // Reutiliza o login para popular o AuthContext (token/user/estado).
      await signIn({ email: formData.email, senha: formData.senha });

      toast({
        title: 'Conta criada!',
        description: 'Cadastro realizado com sucesso.',
        status: 'success',
        duration: 3000,
      });

      navigate('/app');
    } catch (error: any) {
      toast({
        title: 'Erro no cadastro',
        description:
          error.response?.data?.error ||
          error.response?.data?.message ||
          'Ocorreu um erro ao criar a conta.',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxW='container.md' py={10}>
      <VStack spacing={8} align='stretch'>
        <Center>
          <Logo variant='blue' height='80px' mb={4} />
        </Center>
        <Box p={8} borderWidth={1} borderRadius={8} boxShadow='lg'>
          <VStack spacing={4}>
            <Heading size='lg'>Cadastro</Heading>
            <Box as='form' w='100%' onSubmit={handleSubmit}>
              <VStack spacing={4}>
                <FormControl isRequired>
                  <FormLabel>Nome</FormLabel>
                  <InputGroup>
                    <InputLeftElement children={<Icon as={FiUser} />} />
                    <Input
                      name='nome'
                      value={formData.nome}
                      onChange={handleInputChange}
                      placeholder='Digite seu nome completo'
                    />
                  </InputGroup>
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>E-mail</FormLabel>
                  <InputGroup>
                    <InputLeftElement children={<Icon as={FiMail} />} />
                    <Input
                      name='email'
                      type='email'
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder='Digite seu e-mail'
                    />
                  </InputGroup>
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>Tipo de Cadastro</FormLabel>
                  <RadioGroup value={formData.tipo} onChange={handleTipoChange}>
                    <Stack direction='row'>
                      <Radio value='pessoa'>Pessoa Física</Radio>
                      <Radio value='empresa'>Pessoa Jurídica</Radio>
                    </Stack>
                  </RadioGroup>
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>{formData.tipo === 'pessoa' ? 'CPF' : 'CNPJ'}</FormLabel>
                  <InputGroup>
                    <InputLeftElement children={<Icon as={FiUser} />} />
                    <Input
                      name='documento'
                      value={formData.documento}
                      onChange={handleInputChange}
                      placeholder={`Digite seu ${formData.tipo === 'pessoa' ? 'CPF' : 'CNPJ'}`}
                      maxLength={formData.tipo === 'pessoa' ? 11 : 14}
                    />
                  </InputGroup>
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>Senha</FormLabel>
                  <InputGroup>
                    <InputLeftElement children={<Icon as={FiLock} />} />
                    <Input
                      name='senha'
                      type='password'
                      value={formData.senha}
                      onChange={handleSenhaChange}
                      placeholder='Digite sua senha'
                    />
                  </InputGroup>
                  <Progress
                    value={passwordStrength.percentage}
                    size='sm'
                    colorScheme={
                      passwordStrength.percentage < 40
                        ? 'red'
                        : passwordStrength.percentage < 80
                        ? 'yellow'
                        : 'green'
                    }
                    mt={2}
                  />
                  <Box mt={2}>
                    <Text fontSize='sm' fontWeight='medium' mb={2}>
                      Requisitos da senha:
                    </Text>
                    <VStack align='start' spacing={1}>
                      {passwordRequirements.map((req) => (
                        <HStack key={req.id} spacing={2}>
                          <Text fontSize='sm' color={req.met ? 'green.500' : 'gray.500'}>
                            {req.met ? '✓' : '○'}
                          </Text>
                          <Text fontSize='sm' color={req.met ? 'green.500' : 'gray.500'}>
                            {req.label}
                          </Text>
                        </HStack>
                      ))}
                    </VStack>
                  </Box>
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>Confirmar Senha</FormLabel>
                  <InputGroup>
                    <InputLeftElement children={<Icon as={FiLock} />} />
                    <Input
                      name='confirmarSenha'
                      type='password'
                      value={formData.confirmarSenha}
                      onChange={handleInputChange}
                      placeholder='Confirme sua senha'
                    />
                  </InputGroup>
                </FormControl>

                <Button type='submit' colorScheme='blue' w='100%' size='lg' isLoading={loading}>
                  Criar Conta
                </Button>
              </VStack>
            </Box>
            <HStack spacing={2} justify='center'>
              <Text>Já tem uma conta?</Text>
              <Link to='/login'>
                <Text color='blue.500' _hover={{ textDecoration: 'underline' }}>
                  Faça login
                </Text>
              </Link>
            </HStack>
          </VStack>
        </Box>
      </VStack>
    </Container>
  );
};
